import React, { useEffect, useState, useRef, createElement } from 'react';
import { Hero } from '../components/Hero';
import { Features } from '../components/Features';
import { HowItWorks } from '../components/HowItWorks';
import { TopServices } from '../components/TopServices';
import { Comparison } from '../components/Comparison';
import { Methodology } from '../components/Methodology';
import { FAQ } from '../components/FAQ';
import { CallToAction } from '../components/CallToAction';
import { Newsletter } from '../components/Newsletter';
import { AuroraBackground } from '../components/AuroraBackground';
import { ThemeProvider } from '../contexts/ThemeContext';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';
import { MagicLogo } from '../components/MagicLogo';
import { supportedLanguages, Language } from '../lib/translations';
function LanguageDropdown({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  return <div ref={ref} className="relative" data-id="element-593">
      <button onClick={() => setOpen(!open)} className={`flex items-center gap-1.5 text-sm font-medium transition-colors rounded-lg ${compact ? 'text-gray-300 hover:text-white px-2 py-1.5 bg-white/5 border border-white/10' : 'text-gray-400 hover:text-white px-2 py-1 hover:bg-white/5'}`} aria-label="Select language" data-id="element-594">
        <Globe className="w-4 h-4" data-id="element-595" />
        <span data-id="element-596">{language.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-597" />
      </button>

      {open && <div className="absolute right-0 top-full mt-2 w-48 max-h-72 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl z-50 py-1" data-id="element-598">
          {supportedLanguages.map(lang => <button key={lang.code} onClick={() => {
        setLanguage(lang.code as Language);
        setOpen(false);
      }} className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between ${language === lang.code ? 'text-accent-blue bg-accent-blue/10' : 'text-gray-300 hover:text-white hover:bg-white/5'}`} data-id="element-599">
              <span data-id="element-600">{lang.nativeName}</span>
              <span className="text-xs text-gray-500" data-id="element-601">
                {lang.code.toUpperCase()}
              </span>
            </button>)}
        </div>}
    </div>;
}
function Navigation() {
  const {
    t
  } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    label: t.nav.rating,
    href: '#rating'
  }, {
    label: t.nav.comparison,
    href: '#comparison'
  }, {
    label: t.nav.howItWorks,
    href: '#how-it-works'
  }, {
    label: t.nav.faq,
    href: '#faq'
  }];
  return <nav className={`fixed top-0 w-full z-40 border-b transition-all duration-300 ${scrolled ? 'bg-[#050505]/90 backdrop-blur-md border-white/10 shadow-lg' : 'bg-transparent border-transparent'}`} data-id="element-602">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-603">
        <div className="flex justify-between items-center h-16" data-id="element-604">
          <a href="#" className="flex items-center gap-2 group" data-id="element-605">
            <MagicLogo data-id="element-606" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8" data-id="element-607">
            {navLinks.map(link => <a key={link.href} href={link.href} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-id="element-608">
                {link.label}
              </a>)}
            <div className="flex items-center gap-4 border-l border-white/10 pl-6" data-id="element-609">
              <LanguageDropdown data-id="element-610" />
              <a href="#rating" className="px-4 py-2 bg-accent-blue text-white rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors shadow-lg shadow-accent-blue/20" data-id="element-611">
                {t.nav.start}
              </a>
            </div>
          </div>

          {/* Mobile Controls */}
          <div className="md:hidden flex items-center gap-3" data-id="element-612">
            <LanguageDropdown compact data-id="element-613" />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-gray-300 hover:text-white transition-colors" aria-label="Toggle menu" data-id="element-614">
              {mobileOpen ? <X className="w-6 h-6" data-id="element-615" /> : <Menu className="w-6 h-6" data-id="element-616" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && <div className="md:hidden border-t border-white/10 bg-[#0A0A0A] absolute left-0 right-0 px-4 pb-6 shadow-2xl animate-fade-in origin-top" data-id="element-617">
            <div className="flex flex-col space-y-1 pt-4" data-id="element-618">
              {navLinks.map(link => <a key={link.href} href={link.href} onClick={() => setMobileOpen(false)} className="block text-gray-300 hover:text-white hover:bg-white/5 rounded-lg text-base font-medium py-3 px-3 transition-colors" data-id="element-619">
                  {link.label}
                </a>)}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-4" data-id="element-620">
              <a href="#rating" onClick={() => setMobileOpen(false)} className="block w-full text-center py-3 bg-accent-blue text-white rounded-lg font-medium hover:bg-accent-hover transition-colors shadow-lg shadow-accent-blue/20" data-id="element-621">
                {t.nav.start}
              </a>
            </div>
          </div>}
      </div>
    </nav>;
}
function LandingPageContent() {
  const {
    t,
    language
  } = useLanguage();
  // Canonical base URL — replace with your real domain before deploying
  const BASE_URL = 'https://magicaibuilders.com';
  // OG image
  const OG_IMAGE = `${BASE_URL}/og-image.png`;
  const OG_IMAGE_WIDTH = '1200';
  const OG_IMAGE_HEIGHT = '630';
  // Set meta tags for SEO
  useEffect(() => {
    const seoTitle = t.seo.title;
    const seoDesc = t.seo.description;
    document.title = seoTitle;
    // ── Dynamic <html lang> attribute ──────────────────────────
    document.documentElement.lang = getLangCode(language);
    const setMeta = (name: string, content: string, attr: string = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    const removeMeta = (name: string, attr: string = 'name') => {
      document.querySelectorAll(`meta[${attr}="${name}"]`).forEach(el => el.remove());
    };
    // Core SEO
    setMeta('description', seoDesc);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('theme-color', '#050505');
    setMeta('author', 'Magic AI Builders Editorial Team');
    // ── Article dates (E-E-A-T signal) ────────────────────────
    setMeta('article:published_time', '2026-01-01', 'property');
    setMeta('article:modified_time', '2026-02-20', 'property');
    setMeta('article:author', BASE_URL, 'property');
    setMeta('article:section', 'Technology', 'property');
    // Canonical URL
    const langPath = supportedLanguages.find(l => l.code === language)?.path || '/';
    const pageUrl = `${BASE_URL}${langPath}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = pageUrl;
    // ── hreflang <link> tags in <head> ─────────────────────────
    // Remove old hreflang links before re-adding
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    const hreflangMap: Record<string, string> = {
      en: `${BASE_URL}/`,
      ru: `${BASE_URL}/ru`,
      ua: `${BASE_URL}/ua`,
      de: `${BASE_URL}/de`,
      es: `${BASE_URL}/es`,
      fr: `${BASE_URL}/fr`,
      'pt-br': `${BASE_URL}/pt-br`,
      it: `${BASE_URL}/it`,
      nl: `${BASE_URL}/nl`
    };
    const hreflangLang: Record<string, string> = {
      en: 'en',
      ru: 'ru',
      ua: 'uk',
      de: 'de',
      es: 'es',
      fr: 'fr',
      'pt-br': 'pt-BR',
      it: 'it',
      nl: 'nl'
    };
    // x-default always points to English
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.href = `${BASE_URL}/`;
    document.head.appendChild(xDefault);
    Object.entries(hreflangMap).forEach(([code, url]) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.setAttribute('hreflang', hreflangLang[code]);
      link.href = url;
      document.head.appendChild(link);
    });
    // Open Graph
    const ogLocale = language === 'ua' ? 'uk_UA' : language === 'pt-br' ? 'pt_BR' : `${language}_${language.toUpperCase()}`;
    setMeta('og:type', 'website', 'property');
    setMeta('og:site_name', t.brand, 'property');
    setMeta('og:url', pageUrl, 'property');
    setMeta('og:title', seoTitle, 'property');
    setMeta('og:description', seoDesc, 'property');
    setMeta('og:image', OG_IMAGE, 'property');
    setMeta('og:image:width', OG_IMAGE_WIDTH, 'property');
    setMeta('og:image:height', OG_IMAGE_HEIGHT, 'property');
    setMeta('og:image:alt', t.hero.h1, 'property');
    setMeta('og:locale', ogLocale, 'property');
    // og:locale:alternate
    removeMeta('og:locale:alternate', 'property');
    const ogLocaleMap: Record<string, string> = {
      en: 'en_US',
      ru: 'ru_RU',
      ua: 'uk_UA',
      de: 'de_DE',
      es: 'es_ES',
      fr: 'fr_FR',
      'pt-br': 'pt_BR',
      it: 'it_IT',
      nl: 'nl_NL'
    };
    supportedLanguages.filter(l => l.code !== language).forEach(l => {
      const altLocale = ogLocaleMap[l.code] || l.code;
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:locale:alternate');
      el.setAttribute('content', altLocale);
      document.head.appendChild(el);
    });
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@MagicAIBuilders');
    setMeta('twitter:creator', '@MagicAIBuilders');
    setMeta('twitter:title', seoTitle);
    setMeta('twitter:description', seoDesc);
    setMeta('twitter:image', OG_IMAGE);
    setMeta('twitter:image:alt', t.hero.h1);
  }, [language, t]);
  // Helper: BCP-47 language code
  const getLangCode = (lang: string) => {
    if (lang === 'pt-br') return 'pt-BR';
    if (lang === 'ua') return 'uk';
    return lang;
  };
  const langCode = getLangCode(language);
  const langPath = supportedLanguages.find(l => l.code === language)?.path || '/';
  const pageUrl = `${BASE_URL}${langPath}`;
  // Organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}#organization`,
    name: t.brand,
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      '@id': `${BASE_URL}#logo`,
      url: `${BASE_URL}/logo.png`,
      contentUrl: 'https://cdn.magicpatterns.com/uploads/dktzC6iMGBeLT7Yyut1Zbq/logo(2).png',
      width: 512,
      height: 512
    },
    description: t.seo.description,
    foundingDate: '2026',
    knowsLanguage: ['en', 'ru', 'uk', 'de', 'es', 'fr', 'pt', 'it', 'nl'],
    // Add your real social profiles here when available:
    sameAs: [
      // 'https://twitter.com/MagicAIBuilders',
      // 'https://www.linkedin.com/company/magic-ai-builders',
      // 'https://www.facebook.com/MagicAIBuilders',
    ]
  };
  // WebSite schema — required for proper site graph and SearchAction
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}#website`,
    url: BASE_URL,
    name: t.brand,
    description: t.seo.description,
    inLanguage: 'en',
    publisher: {
      '@id': `${BASE_URL}#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/?s={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
  // WebPage schema — isPartOf now correctly points to WebSite
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': pageUrl,
    name: t.seo.title,
    description: t.seo.description,
    url: pageUrl,
    inLanguage: langCode,
    datePublished: '2026-01-01',
    dateModified: '2026-02-20',
    author: {
      '@id': `${BASE_URL}#organization`
    },
    publisher: {
      '@id': `${BASE_URL}#organization`
    },
    isPartOf: {
      '@id': `${BASE_URL}#website`
    },
    mainEntity: {
      '@type': 'ItemList',
      name: t.topServices.title,
      itemListOrder: 'https://schema.org/ItemListOrderDescending',
      numberOfItems: t.topServices.services.length,
      itemListElement: t.topServices.services.map((service: any, index: number) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'SoftwareApplication',
          name: service.name,
          description: service.bestFor,
          applicationCategory: 'WebApplication',
          operatingSystem: 'Web',
          url: service.link,
          // ratingCount intentionally omitted — no verified review count to display
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: service.rating,
            bestRating: '5',
            worstRating: '1'
          },
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock'
          }
        }
      }))
    }
  };
  // FAQPage schema (single source of truth — removed from FAQ.tsx)
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    url: `${pageUrl}#faq`,
    inLanguage: langCode,
    mainEntity: t.faq.items.map((item: any) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a.replace(/\n/g, ' ').replace(/•/g, '').trim()
      }
    }))
  };
  // HowTo schema for the "How It Works" steps section
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `${pageUrl}#how-it-works`,
    name: t.howItWorks.title,
    description: t.howItWorks.subtitle,
    inLanguage: langCode,
    step: t.howItWorks.steps.map((step: any, index: number) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.desc
    }))
  };
  // Article / ReviewNewsArticle schema — strengthens E-E-A-T signals
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ReviewNewsArticle',
    '@id': `${pageUrl}#article`,
    headline: t.seo.title,
    description: t.seo.description,
    url: pageUrl,
    inLanguage: langCode,
    datePublished: '2026-01-01',
    dateModified: '2026-02-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl
    },
    author: {
      '@type': 'Organization',
      '@id': `${BASE_URL}#organization`,
      name: t.brand
    },
    publisher: {
      '@id': `${BASE_URL}#organization`
    },
    image: {
      '@type': 'ImageObject',
      url: OG_IMAGE,
      width: 1200,
      height: 630
    },
    reviewedBy: {
      '@type': 'Organization',
      '@id': `${BASE_URL}#organization`,
      name: t.brand
    }
  };
  // Review schema for top 3 services (individual editorial reviews)
  const reviewsSchema = t.topServices.services.slice(0, 3).map((service: any) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    '@id': `${pageUrl}#review-${service.name.toLowerCase().replace(/\s+/g, '-')}`,
    inLanguage: langCode,
    datePublished: '2026-01-01',
    dateModified: '2026-02-20',
    author: {
      '@type': 'Organization',
      '@id': `${BASE_URL}#organization`,
      name: t.brand
    },
    publisher: {
      '@id': `${BASE_URL}#organization`
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: service.rating,
      bestRating: '5',
      worstRating: '1'
    },
    reviewBody: service.bestFor,
    itemReviewed: {
      '@type': 'SoftwareApplication',
      name: service.name,
      url: service.link,
      applicationCategory: 'WebApplication',
      operatingSystem: 'Web',
      // ratingCount intentionally omitted — no verified review count to display
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: service.rating,
        bestRating: '5',
        worstRating: '1'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      }
    }
  }));
  // BreadcrumbList schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: t.brand,
      item: BASE_URL
    }, {
      '@type': 'ListItem',
      position: 2,
      name: t.hero.h1,
      item: pageUrl
    }]
  };
  return <div className="min-h-screen bg-[#050505] selection:bg-accent-light selection:text-accent-blue transition-colors duration-300 relative" data-id="element-622">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(organizationSchema)
    }} data-id="element-623" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(websiteSchema)
    }} data-id="element-624" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-625" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-626" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-627" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(howToSchema)
    }} data-id="element-628" />
      {reviewsSchema.map((review: any) => <script key={review['@id']} type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(review)
    }} data-id="element-629" />)}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbSchema)
    }} data-id="element-630" />
      <AuroraBackground data-id="element-631" />
      <Navigation data-id="element-632" />
      <main className="relative z-10" data-id="element-633">
        <Hero data-id="element-634" />
        <Features data-id="element-635" />
        <section id="how-it-works" data-id="element-636">
          <HowItWorks data-id="element-637" />
        </section>
        <TopServices data-id="element-638" />
        <section id="comparison" data-id="element-639">
          <Comparison data-id="element-640" />
        </section>
        <Methodology data-id="element-641" />
        <Newsletter data-id="element-642" />
        <FAQ data-id="element-643" />
        <CallToAction data-id="element-644" />
      </main>
      <footer className="relative z-10 bg-black border-t border-white/5 py-12 transition-colors duration-300" data-id="element-645">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-646">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-647">
            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity" data-id="element-648">
              <MagicLogo data-id="element-649" />
            </div>
            <nav className="flex flex-wrap justify-center gap-6 text-sm text-gray-400" data-id="element-650">
              <a href="#rating" className="hover:text-white transition-colors" data-id="element-651">
                {t.nav.rating}
              </a>
              <a href="#comparison" className="hover:text-white transition-colors" data-id="element-652">
                {t.nav.comparison}
              </a>
              <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-653">
                {t.nav.howItWorks}
              </a>
              <a href="#faq" className="hover:text-white transition-colors" data-id="element-654">
                {t.nav.faq}
              </a>
            </nav>
            <p className="text-gray-400 text-sm" data-id="element-655">
              © {new Date().getFullYear()} {t.brand}. Independent Reviews.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}
export function LandingPage() {
  return <ThemeProvider data-id="element-656">
      <LanguageProvider data-id="element-657">
        <LandingPageContent data-id="element-658" />
      </LanguageProvider>
    </ThemeProvider>;
}